// Prefetch hero slides data at module load time (before React mounts)
// This eliminates the delay between JS load and API call for LCP-critical hero image

import { supabase } from '@/integrations/supabase/client';

export interface PrefetchedHeroSlide {
  id: string;
  title: string;
  subtitle: string | null;
  image_url: string;
  cta_page_url: string | null;
  cta_text: string | null;
  is_active: boolean;
  display_order: number;
}

// Start fetching immediately when this module is imported
let heroSlidesPromise: Promise<PrefetchedHeroSlide[]> | null = null;

async function startPrefetch(): Promise<PrefetchedHeroSlide[]> {
  const { data, error } = await supabase
    .from('hero_slides')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) throw error;
  const slides = (data || []) as PrefetchedHeroSlide[];

  // Immediately inject a preload link for the first image (LCP optimization)
  if (slides.length > 0 && slides[0].image_url) {
    const existing = document.querySelector('link[data-hero-preload]');
    if (!existing) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = slides[0].image_url;
      link.setAttribute('fetchpriority', 'high');
      link.setAttribute('data-hero-preload', 'true');
      document.head.appendChild(link);
    }
  }

  return slides;
}

// Defer prefetch behind requestIdleCallback so it doesn't compete with main JS parse.
// Only run on the homepage — other pages don't show a hero and shouldn't pay for the query.
if (typeof window !== 'undefined' && window.location.pathname === '/') {
  const kick = () => { heroSlidesPromise = startPrefetch(); };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(kick, { timeout: 200 });
  } else {
    setTimeout(kick, 0);
  }
}

export function getPrefetchedHeroSlides(): Promise<PrefetchedHeroSlide[]> {
  if (!heroSlidesPromise) {
    heroSlidesPromise = startPrefetch();
  }
  return heroSlidesPromise;
}
